// EventBar.js
import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox"; // Adjust the import path based on your project structure
import MDTypography from "components/MDTypography"; // Adjust the import path based on your project structure
import { Link } from "react-router-dom";

const HtmlTooltip = styled(({ className, darkMode, ...props }) => (
  <Tooltip key={darkMode} {...props} classes={{ popper: className }} />
))(({ theme, darkMode }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 275,
    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
    boxShadow: darkMode ? "0 0.5px 2px 0.5px white" : "0 0.5px 2px 0.5px #344767",
    fontSize: 12,
  },
  [`& .MuiTooltip-arrow`]: {
    color: darkMode ? "white" : "#273956",
  },
}));

const EventBar = ({
  startDate,
  endDate,
  currentDays,
  conflictType,
  color,
  scndLabel,
  label,
  eventId,
  event,
  darkMode,
  user,
  stages,
  absences,
  setCurrentOrderId,
  setCurrentMission,
  setMissionEditOpen,
  setCurrentAbsence,
  setAbsenceEditOpen,
}) => {
  const startIndex = currentDays.indexOf(startDate);
  const endIndex = currentDays.indexOf(endDate);
  const span = endIndex - startIndex + 1;
  const barWidth = span * 37.5 - 3; // Width of the bar

  const barStyle = {
    "&:hover": {
      textDecoration:
        conflictType === "Mission" || conflictType === "Absence" ? "underline" : "none",
      textDecorationColor: "white",
    },
    margin: "0 .125rem 0 .0625rem",
    top: "6px",
    position: "absolute",
    width: `${barWidth}px`,
    height: "35.5px",
    backgroundImage: color,
    display: "flex",
    alignItems: "center",
    paddingLeft: "8.75px",
    color: "white",
    zIndex: 2,
    borderRadius: "6px",
    cursor: "pointer",
  };

  const truncatedLabel =
    label?.length > barWidth / 8 ? `${label.substring(0, barWidth / 8 - 3)}...` : label;

  function truncateTextToWidth(
    text,
    maxWidthPx,
    fontSizeRem,
    fontFace = "Roboto",
    fontWeight = "700"
  ) {
    // Convert rem to pixels (assuming a base font size of 16px)
    const fontSizePx = parseFloat(fontSizeRem) * 16;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = `${fontWeight} ${fontSizePx}px ${fontFace}`;

    // Check if the text width is within the maxWidth
    if (context.measureText(text).width <= maxWidthPx) {
      return text; // Return the original text if it fits
    }

    // Truncate text until it fits
    let truncatedText = text;
    while (truncatedText.length > 0) {
      truncatedText = truncatedText.substring(0, truncatedText.length - 1);
      if (context.measureText(truncatedText + "...").width <= maxWidthPx) {
        return truncatedText + "...";
      }
    }

    return ""; // Return an empty string if nothing fits (shouldn't really happen with reasonable input)
  }

  const positionRef = React.useRef({
    x: 0,
    y: 0,
  });
  const popperRef = React.useRef(null);
  const areaRef = React.useRef(null);

  const handleMouseMove = (event) => {
    positionRef.current = { x: event.clientX, y: event.clientY };

    if (popperRef.current != null) {
      popperRef.current.update();
    }
  };

  return (
    user && (
      <HtmlTooltip
        key={darkMode}
        darkMode={darkMode}
        placement="top"
        title={
          <MDBox
            sx={{
              background: darkMode
                ? "white !important"
                : "linear-gradient(195deg, #364c70, #273956) !important",
              color: darkMode ? "#202940 !important" : "white !important",
              paddingBottom: 0.5,
            }}
          >
            <MDTypography
              key={darkMode}
              variant="h6"
              sx={{ lineHeight: "1.2rem", margin: "0.2rem" }}
              color={darkMode ? "#202940" : "white"}
            >
              {label}
            </MDTypography>
            {event.map((e, index) => {
              return e.order_id == null && e.device_number == null ? (
                user && (user.role === "Admin" || user.role === "Pilote") ? (
                  <Grid
                    key={darkMode + e.id}
                    onClick={() => {
                      setAbsenceEditOpen(true);
                      setCurrentAbsence(absences.find((absence) => absence.absence_id === e.id));
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <MDTypography
                      key={darkMode + e.id}
                      sx={{ "&:hover": { textDecoration: "underline" } }}
                      variant="p"
                      color={darkMode ? "#202940" : "white"}
                    >
                      Absence : <b>{e.description}</b>
                    </MDTypography>
                  </Grid>
                ) : (
                  <Grid key={darkMode + e.id}>
                    <MDTypography
                      key={darkMode + e.id}
                      variant="p"
                      color={darkMode ? "#202940" : "white"}
                    >
                      Vous n'avez pas les droits pour modifier cette absence
                    </MDTypography>
                  </Grid>
                )
              ) : user &&
                (user.role === "Admin" ||
                  user.role === "Pilote" ||
                  user.role === "Chef d'agence" ||
                  user.role === "Contremaître") ? (
                (() => {
                  const canEdit =
                    user.role === "Admin" ||
                    user.role === "Pilote" ||
                    user.role === "Chef d'agence" ||
                    (user.role === "Contremaître" &&
                      parseInt(user.user_id) === parseInt(e.foreman_id));
                  return (
                    <Grid
                      key={darkMode + e.id}
                      onClick={
                        canEdit
                          ? () => {
                              setMissionEditOpen(true);
                              setCurrentOrderId(e.order_id);
                              setCurrentMission(
                                stages.find((stage) => stage.stage_id === e.id)
                              );
                            }
                          : undefined
                      }
                      sx={{
                        cursor: canEdit ? "pointer" : "default",
                      }}
                    >
                      <MDTypography
                        key={darkMode + e.id}
                        sx={canEdit ? { "&:hover": { textDecoration: "underline" } } : {}}
                        variant="p"
                        color={darkMode ? "#202940" : "white"}
                      >
                        {Array.isArray(scndLabel) && (
                          <>
                            <i>
                              <b style={{ fontSize: "0.85rem" }}>
                                {truncateTextToWidth(scndLabel[index], 180, 0.85)}
                              </b>
                            </i>
                            <br />
                          </>
                        )}
                        <b>Mission :</b> {e.device_number} - {e.description}
                        <br />
                        <b>Client :</b> {e.client !== null ? e.client : "Non spécifiée"}
                        <br />
                        <b>Chantier :</b> {e.site_name !== null ? e.site_name : "Non spécifiée"}
                      </MDTypography>
                    </Grid>
                  );
                })()
              ) : user && (user.role === "Assistante de gestion" || user.role === "Direction") ? (
                e.order_id ? (
                  <Grid
                    key={darkMode + e.id}
                    onClick={() => {
                      setCurrentOrderId(e.order_id);
                    }}
                    component={Link}
                    to={`/commandes/${encodeURIComponent(e.order_id)}`}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <MDTypography
                      key={darkMode + e.id}
                      sx={{ "&:hover": { textDecoration: "underline" } }}
                      variant="p"
                      color={darkMode ? "#202940" : "white"}
                    >
                      <b>Mission :</b> {e.device_number} - {e.description}
                      <br />
                      <b>Client :</b> {e.client !== null ? e.client : "Non spécifiée"}
                      <br />
                      <b>Chantier :</b> {e.site_name !== null ? e.site_name : "Non spécifiée"}
                      <br />
                      <i>Cliquez ici pour accéder à la commande</i>
                    </MDTypography>
                  </Grid>
                ) : (
                  <Grid key={darkMode + e.id}>
                    <MDTypography
                      key={darkMode + e.id}
                      variant="p"
                      color={darkMode ? "#202940" : "white"}
                    >
                      Absence : <b>{e.description}</b>
                    </MDTypography>
                  </Grid>
                )
              ) : (
                <Grid key={darkMode + e.id}>
                  <MDTypography
                    key={darkMode + e.id}
                    variant="p"
                    color={darkMode ? "#202940" : "white"}
                  >
                    Vous n'avez pas les droits pour accéder à cette phase
                  </MDTypography>
                </Grid>
              );
            })}
          </MDBox>
        }
        arrow
        PopperProps={{
          popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              return new DOMRect(
                positionRef.current.x,
                areaRef.current.getBoundingClientRect().y,
                0,
                0
              );
            },
          },
        }}
      >
        <Grid
          onClick={() => {
            if (
              conflictType === "Mission" &&
              user &&
              (user.role === "Admin" ||
                user.role === "Pilote" ||
                user.role === "Chef d'agence" ||
                (user.role === "Contremaître" &&
                  parseInt(user.user_id) === parseInt(event[0].foreman_id)))
            ) {
              setMissionEditOpen(true);
              setCurrentOrderId(event[0].order_id);
              setCurrentMission(stages.find((stage) => stage.stage_id === event[0].id));
            } else if (
              conflictType === "Absence" &&
              user &&
              (user.role === "Admin" || user.role === "Pilote")
            ) {
              setAbsenceEditOpen(true);
              setCurrentAbsence(absences.find((absence) => absence.absence_id === event[0].id));
            }
          }}
          sx={barStyle}
          ref={areaRef}
          onMouseMove={handleMouseMove}
          data-event-id={eventId}
        >
          <MDTypography variant="p" color="white">
            {truncatedLabel !== "" ? truncatedLabel : "Adresse inconnue"}
          </MDTypography>
        </Grid>
      </HtmlTooltip>
    )
  );
};

export default EventBar;
